import { useState, useEffect } from 'react'
import { getEventos, crearEvento, editarEvento, borrarEvento } from '../../api/client'
import Modal from '../Modal'
import { detalleError } from '../admin/detalle'
import { rangoFechas } from '../../utils/ausencias'

// Company events (backend eventos.py): the Christmas celebration, the
// end-of-year outing. Admin and RRHH manage them here; everyone sees them in
// the calendar and Plan Week. "Libre para todos" (whole days only) counts like
// a holiday in everyone's hours; otherwise it's an activity, only shown.

const VACIO = { nombre: '', start_date: '', end_date: '', jornada: 'full', libre: false, notas: '' }

export default function EventosModal({ t, onClose, onCambio }) {
  const anio = new Date().getFullYear()
  const [lista, setLista] = useState(null)
  const [form, setForm] = useState(VACIO)
  const [editando, setEditando] = useState(null)      // event id, or null for a new one
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const cargar = () => getEventos({ desde: `${anio - 1}-01-01` }).then(setLista).catch(() => setLista([]))
  useEffect(() => { cargar() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const unDia = form.start_date && form.start_date === form.end_date
  const fechas = (cambio) => setForm(f => {
    const g = { ...f, ...cambio }
    if (cambio.start_date && (!g.end_date || g.end_date < g.start_date)) g.end_date = g.start_date
    if (g.start_date !== g.end_date) g.jornada = 'full'
    if (g.jornada !== 'full') g.libre = false
    return g
  })

  const editar = (e) => {
    setEditando(e.id)
    setForm({ nombre: e.nombre, start_date: e.start_date, end_date: e.end_date, jornada: e.mitad || 'full',
              libre: e.libre, notas: e.notas || '' })
    setError('')
  }

  const guardar = async (ev) => {
    ev.preventDefault()
    if (!form.nombre.trim() || !form.start_date || !form.end_date) { setError(t('eventos.faltan')); return }
    setBusy(true); setError('')
    const data = {
      nombre: form.nombre.trim(), start_date: form.start_date, end_date: form.end_date,
      mitad: unDia && form.jornada !== 'full' ? form.jornada : null,
      libre: form.jornada === 'full' && form.libre, notas: form.notas.trim() || null,
    }
    try {
      if (editando) await editarEvento(editando, data)
      else await crearEvento(data)
      setForm(VACIO); setEditando(null)
      cargar(); onCambio()
    } catch (e) {
      setError(detalleError(e, t('eventos.failed')))
    }
    setBusy(false)
  }

  const quitar = async (e) => {
    if (!window.confirm(t('eventos.borrarConfirm', { nombre: e.nombre }))) return
    try {
      await borrarEvento(e.id)
      if (editando === e.id) { setEditando(null); setForm(VACIO) }
      cargar(); onCambio()
    } catch (err) {
      setError(detalleError(err, t('eventos.failed')))
    }
  }

  const campo = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'
  return (
    <Modal title={t('eventos.titulo')} onClose={onClose} wide>
      <div className="space-y-5">
        <p className="text-xs text-gray-500">{t('eventos.ayuda')}</p>

        <form onSubmit={guardar} className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('eventos.nombre')}</label>
              <input className={campo} value={form.nombre} placeholder={t('eventos.nombrePlaceholder')}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('pedir.inicio')}</label>
              <input type="date" className={campo} value={form.start_date} onChange={e => fechas({ start_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('pedir.fin')}</label>
              <input type="date" className={campo} value={form.end_date} min={form.start_date || undefined}
                onChange={e => fechas({ end_date: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {unDia && (
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                {['full', 'am', 'pm'].map(j => (
                  <button key={j} type="button" onClick={() => fechas({ jornada: j })}
                    className={`px-3 py-1 rounded-md text-xs font-medium ${form.jornada === j ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                    {t(`pedir.jornada.${j}`)}
                  </button>
                ))}
              </div>
            )}
            <label className={`flex items-center gap-2 text-sm ${form.jornada !== 'full' ? 'text-gray-300' : 'text-gray-700'}`}>
              <input type="checkbox" className="accent-violet-600" checked={form.libre} disabled={form.jornada !== 'full'}
                onChange={e => setForm(f => ({ ...f, libre: e.target.checked }))} />
              {t('eventos.libre')}
            </label>
          </div>
          {form.jornada !== 'full' && <p className="text-[11px] text-gray-400">{t('eventos.medioDiaNoLibre')}</p>}
          <input className={campo} value={form.notas} placeholder={t('eventos.notasPlaceholder')}
            onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex justify-end gap-2">
            {editando && (
              <button type="button" onClick={() => { setEditando(null); setForm(VACIO) }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
                {t('pedir.cancelar')}
              </button>
            )}
            <button type="submit" disabled={busy}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50">
              {editando ? t('eventos.guardar') : t('eventos.agregar')}
            </button>
          </div>
        </form>

        {lista === null ? (
          <p className="text-sm text-gray-400">{t('cargando')}</p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-gray-400">{t('eventos.vacio')}</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
            {[...lista].reverse().map(e => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${e.libre ? 'bg-violet-500' : 'border-2 border-violet-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{e.nombre}</p>
                  <p className="text-xs text-gray-500">
                    {rangoFechas(e.start_date, e.end_date)}
                    {e.mitad ? ` · ${t(`pedir.jornada.${e.mitad}`)}` : ''}
                    {' · '}{e.libre ? t('eventos.esLibre') : t('eventos.esActividad')}
                    {e.notas ? ` · ${e.notas}` : ''}
                  </p>
                </div>
                <button onClick={() => editar(e)} className="text-xs font-medium text-gray-500 hover:text-gray-900">{t('eventos.editar')}</button>
                <button onClick={() => quitar(e)} className="text-xs font-medium text-gray-400 hover:text-red-600">{t('eventos.borrar')}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
